// src/routes/routeDefinitions.tsx
// ─────────────────────────────────────────────────────────────────────────────
// All <Route> declarations live here.
// App.tsx just renders <AppRoutes /> — it knows nothing about individual pages.
// ─────────────────────────────────────────────────────────────────────────────
import { Route, Navigate, Routes, Outlet, useLocation } from 'react-router-dom';
import { ProtectedFunnelRoute } from '../components/ProtectedFunnelRoute';
import CareerCompanionDashboard from '../Home/CareerCompanionDashboard';
import routeConfig from './routeConfig';
import componentsMap from '../utils/componentsMap';
import { defaultHomeForRole, saveReturnTo } from '../utils/returnTo';

import {
  Login,
  GoogleCallback,
  TermsAndConditionsPage,
  JobPage,
  ApplyPage,
  JoinWhatsAppPage,
  ShortLinkRedirect,
  OnboardingRouter,
  ResumeBuilderRouter,
  RazorpayPayment,
  PaymentDashboard,
  InvoiceList,
  InvoiceEditor,
  ExecEdgeDashboardTable,
  ExecEdgeFunnelDashboard,
  ExecEdgeCommsDashboard,
  ExecEdgeLandingPage,
  ExecEdgePreDemo,
  ExecEdgeThankYou,
  ExecEdgeDemoPlayer,
  ExecEdgeProgramPlan,
  ExecEdgeProgramPlanTest,
  ExecEdgeFAQs,
  ExecEdgeDemoFeedback,
  ExecEdgeFunnelSuccess,
  ExecEdgeProfileReviewPending,
  ExecEdgeProfileReview,
  ExecEdgeFlow,
  AiFlowRouter,
  WhatsAppDashboardPage,
  WhatsAppConversationsPage,
  WhatsAppConversationDetailPage,
  WhatsAppContactsPage,
  WhatsAppContactDetailPage,
  WhatsAppTemplatesPage,
  WhatsAppQueuePage,
  WhatsAppEventsPage,
  WhatsAppHealthPage,
  WorkerControlCenter,
} from '../utils/componentsMap';

interface User {
  role: string;
  termsAccepted?: boolean;
}

// Renders routes driven by routeConfig.jsx
const renderConfigRoute = (route: any, user: User, basePath = '') => {
  const fullPath = `${basePath}${route.path ?? ''}`.replace(/\/\//g, '/');
  const Component = componentsMap[route.component];

  if (route.children) {
    const hasAccess = !route.roles || route.roles.includes(user.role);
    return (
      <Route
        key={fullPath}
        path={fullPath}
        element={hasAccess && Component ? <Component /> : <Outlet />}
      >
        {route.children.map((child: any) => renderConfigRoute(child, user, fullPath))}
      </Route>
    );
  }

  if (!Component) return null;

  const allowed = !route.roles || route.roles.includes(user.role);
  return (
    <Route
      key={fullPath}
      path={fullPath}
      element={
        allowed ? (
          <Component />
        ) : user.role === 'guest' ? (
          <Login />
        ) : (
          <Navigate to={defaultHomeForRole(user.role)} replace />
        )
      }
    />
  );
};

function LegacyJobsRedirect() {
  const location = useLocation();
  return <Navigate to={`/hv/jobs${location.search}${location.hash}`} replace />;
}

function AccessFallback({ user }: { user: User }) {
  const location = useLocation();
  if (!user?.role || user.role === 'guest') {
    saveReturnTo(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={defaultHomeForRole(user.role)} replace />;
}

// ── Route groups ──────────────────────────────────────────────────────────────
// Splitting into groups makes it easy to reason about access rules.

export const authRoutes = (user: User) => (
  <>
    <Route
      path="/login"
      element={
        user.role && user.role !== 'guest'
          ? <Navigate to={defaultHomeForRole(user.role)} replace />
          : <Login />
      }
    />
    <Route path="auth/google/callback" element={<GoogleCallback />} />
    <Route path="/terms"               element={<TermsAndConditionsPage />} />
  </>
);

export const publicRoutes = (
  <>
    <Route path="/hv/jobs"         element={<JobPage />} />
    <Route path="/jobs"            element={<LegacyJobsRedirect />} />
    <Route path="/jobs/"           element={<LegacyJobsRedirect />} />
    <Route path="/apply/:roleName" element={<ApplyPage />} />
    <Route path="/join-whatsapp" element={<JoinWhatsAppPage />} />
    <Route path="/l/:slug" element={<ShortLinkRedirect />} />
  </>
);

export const protectedRoutes = (user: User) => (
  <>
    <Route
      path="/candidate/newexecedge"
      element={user.role !== 'guest' ? <Navigate to="/NewExecedge/project-timeline" replace /> : <AccessFallback user={user} />}
    />
    <Route path="/candidate/career" element={user.role !== "guest" ? <CareerCompanionDashboard /> : <AccessFallback user={user} />} />
    <Route path="/resume-builder/*"   element={<ResumeBuilderRouter />} />
    <Route path="/payment"            element={<RazorpayPayment />} />
    <Route path="/payment-dashboard"  element={<PaymentDashboard />} />
    <Route
      path="/invoices"
      element={user.role === 'admin' ? <InvoiceList /> : <AccessFallback user={user} />}
    />
    <Route
      path="/invoices/:id"
      element={user.role === 'admin' ? <InvoiceEditor /> : <AccessFallback user={user} />}
    />
  </>
);

export const adminRoutes = (user: User) => (
  <>
    <Route
      path="/admin/role-pipeline-report"
      element={user.role === 'admin' ? <Navigate to="/hv/role-pipeline-report" replace /> : <AccessFallback user={user} />}
    />
    <Route
      path="/execedge/role-pipeline-report"
      element={user.role === 'admin' ? <Navigate to="/execedge/pipeline-report" replace /> : <AccessFallback user={user} />}
    />
    <Route
      path="/admin/worker-control-center"
      element={user.role === 'admin' ? <WorkerControlCenter /> : <AccessFallback user={user} />}
    />
    <Route
      path="/execedge/dashboard"
      element={
        user.role === 'guest'  ? <AccessFallback user={user} />   :
        (user.role === 'admin' || user.role === 'recruiter' || user.role === 'guide') ? <ExecEdgeDashboardTable /> :
                                 <Navigate to="/Newexecedge/project-timeline" replace />
      }
    />
    <Route
      path="/execedge-funnel"
      element={user.role === 'admin' ? <ExecEdgeFunnelDashboard /> : <AccessFallback user={user} />}
    />
    <Route
      path="/execedge-comms"
      element={user.role === 'admin' ? <ExecEdgeCommsDashboard /> : <AccessFallback user={user} />}
    />
  </>
);

// All /eedge/* routes — fully public, no auth needed
export const funnelRoutes = (
  <>
    <Route path="/eedge"     element={<ExecEdgeFlow />} />
    <Route path="/eedge/predemo"   element={<ExecEdgePreDemo />} />
    {/* Cross-device resume entry: /eedge/:leadId hydrates funnel from backend */}
    <Route path="/eedge/:leadId" element={<ExecEdgeFlow />} />
    {/* <Route path="/salesfunnel/demo-player" element={<ExecEdgeDemoPlayer />} /> */}
    <Route path="/salesfunnel"           element={<ExecEdgeLandingPage />} />
    {/* <Route path="/eedge/register"  element={<ExecEdgeRegister />} />
    <Route path="/eedge/predemo"   element={<ExecEdgePreDemo />} />
    <Route path="/eedge/start"     element={<ExecEdgeOnboarding />} /> */}
    <Route path="/eedge/thank-you" element={<ExecEdgeThankYou />} />
    <Route path="/eedge/profile-review" element={<ExecEdgeProfileReviewPending />} />
    <Route path="/eedge/profile-review/:token" element={<ExecEdgeProfileReview />} />
    {/* <Route path="/eedge/intro"     element={<ExecEdgeIntroductionDeck />} /> */}
    <Route path="/eedge/faqs"      element={<ExecEdgeFAQs />} />
    <Route path="/eedge/feedback"  element={<ExecEdgeDemoFeedback program={undefined} onComplete={() => {}} />} />
    <Route path="/eedge/success"   element={<ExecEdgeFunnelSuccess />} />
    {/* Protected funnel routes — require funnel token */}
    {/* <Route path="/eedge/deck"         element={<ProtectedFunnelRoute><ExecEdgeProductDeck /></ProtectedFunnelRoute>} />
    <Route path="/eedge/demo-book"    element={<ProtectedFunnelRoute><ExecEdgeDemoBooking /></ProtectedFunnelRoute>} /> */}
    <Route path="/eedge/demo"         element={<ProtectedFunnelRoute><ExecEdgeDemoPlayer /></ProtectedFunnelRoute>} />
    <Route path="/eedge/program-plan" element={<ProtectedFunnelRoute emailGate><ExecEdgeProgramPlan /></ProtectedFunnelRoute>} />
    <Route path="/eedge/program-plan/:id" element={<ExecEdgeProgramPlan />} />
    <Route path="/eedge/program-plan-test" element={<ProtectedFunnelRoute><ExecEdgeProgramPlanTest /></ProtectedFunnelRoute>} />
  </>
);

export const aiRoutes = (
  <Route path="/ai-flow/*" element={<AiFlowRouter selectedEmail={undefined} />} />
);

export const whatsappRoutes = (user: User) => (
  <>
    <Route
      path="/whatsapp/dashboard"
      element={user.role === 'admin' ? <WhatsAppDashboardPage /> : <AccessFallback user={user} />}
    />
    <Route
      path="/whatsapp/conversations"
      element={user.role === 'admin' ? <WhatsAppConversationsPage /> : <AccessFallback user={user} />}
    />
    <Route
      path="/whatsapp/conversations/:id"
      element={user.role === 'admin' ? <WhatsAppConversationDetailPage /> : <AccessFallback user={user} />}
    />
    <Route
      path="/whatsapp/contacts"
      element={user.role === 'admin' ? <WhatsAppContactsPage /> : <AccessFallback user={user} />}
    />
    <Route
      path="/whatsapp/contacts/:id"
      element={user.role === 'admin' ? <WhatsAppContactDetailPage /> : <AccessFallback user={user} />}
    />
    <Route
      path="/whatsapp/templates"
      element={user.role === 'admin' ? <WhatsAppTemplatesPage /> : <AccessFallback user={user} />}
    />
    <Route
      path="/whatsapp/queue"
      element={user.role === 'admin' ? <WhatsAppQueuePage /> : <AccessFallback user={user} />}
    />
    <Route
      path="/whatsapp/events"
      element={user.role === 'admin' ? <WhatsAppEventsPage /> : <AccessFallback user={user} />}
    />
    <Route
      path="/whatsapp/health"
      element={user.role === 'admin' ? <WhatsAppHealthPage /> : <AccessFallback user={user} />}
    />
  </>
);

// ── Main export — everything composed ────────────────────────────────────────
export function AppRoutes({ user }: { user: User }) {
  return (
    <Routes>
      {authRoutes(user)}
      {publicRoutes}
      {protectedRoutes(user)}
      {adminRoutes(user)}
      {funnelRoutes}
      {aiRoutes}
      {whatsappRoutes(user)}
      {routeConfig.map((route: any) => renderConfigRoute(route, user))}
    </Routes>
  );
}
